import * as amplitude from '@amplitude/analytics-browser';
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useStatsigClient } from "@statsig/react-bindings";
import { CheckoutProvider, useCheckout } from "@stripe/react-stripe-js/checkout";
import { loadStripe } from "@stripe/stripe-js";
import { nanoid } from "nanoid";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useChartCalculations } from "./charts/utils";
import { AfterTrialPeriod } from "./features/checkout/AfterTrialPeriod";
import { CheckoutForm } from "./features/checkout/CheckoutForm";
import trophyImage from "./features/checkout/images/trophy.svg";
import { CDN_ROOT } from "./features/images";
import { useCheckoutPlan } from "./hooks/useCheckoutPlan";
import { createGetClientSecretPromise } from "./hooks/useCreactStripeClientSecret";
import { CheckoutPlan, getPlans, getTransformationData } from "./LandingPage";
import { Age } from "./quiz/age";
import { track } from "./services/facebookPixel";
import { User } from "./types";
import { getAssignedExperimentArm, getInternalCoreEndpoint } from "./utils";
import { Model } from "survey-core";
import { shouldUseStripeTestMode } from "./checkoutTestMode";

const STRIPE_TEST_PUBLISHABLE_KEY =
    "pk_test_51OKsGtJnrTlLWSIiRvG09Ew7FlApAv7H73wWpchZ3wZ6w1pWQsyhGBk13ky0kG910Ek89M8sjeLqMxTlybW8M20u002KVRvAmt";

function getStripePublishableKey(search: string) {
    if (shouldUseStripeTestMode(search)) {
        return STRIPE_TEST_PUBLISHABLE_KEY;
    }

    return process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || STRIPE_TEST_PUBLISHABLE_KEY;
}

type CheckoutPageProps = {
    user: User;
    survey: Model;
    onComplete: () => void;
};

type CheckoutPageInnerProps = CheckoutPageProps & {
    onReinitializeCheckout: (newClientSecret: string) => void;
    plan: CheckoutPlan;
    isNoTrialVariation: boolean;
    promoCode: string;
    setPromoCode: (code: string) => void;
    isPromoApplied: boolean;
    setIsPromoApplied: (applied: boolean) => void;
    promoError: string;
    setPromoError: (error: string) => void;
};

const Wrapper = styled.div`
    background-color: #f6f6f8;
    min-height: 100vh;
    width: 100%;
`;

const CheckoutPageContainer = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
    min-height: 100vh;

    @media (max-width: 768px) {
        padding: 10px;
    }
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 30px;

    @media (max-width: 768px) {
        margin-bottom: 20px;
    }
`;

const Logo = styled.div`
    display: flex;
    align-items: center;
    color: #406edf;
    font-weight: bold;
    font-size: 24px;

    @media (max-width: 768px) {
        font-size: 20px;
    }
`;

const MainContent = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 40px;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 20px;
    }
`;

const LeftPanel = styled.div`
    display: flex;
    flex-direction: column;
`;

const RightPanel = styled.div`
    display: flex;
    flex-direction: column;
`;

const PersonalPlanCard = styled.div`
    background: white;
    border-radius: 16px;
    margin-bottom: 30px;

    @media (max-width: 768px) {
        margin-bottom: 0px;
    }
`;

const PersonalPlanCardInner = styled.div`
    padding: 24px;
`;

const PlanTitle = styled.h1`
    font-size: 24px;
    font-weight: bold;
    color: #1e1e1e;
    margin-bottom: 0;

    @media (max-width: 768px) {
        font-size: 16px;
    }
`;

const TargetGoal = styled.div`
    font-size: 32px;
    font-weight: 800;
    color: #406edf;
    margin-bottom: 25px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    @media (max-width: 768px) {
        font-size: 28px;
        margin-bottom: 20px;
    }
`;

const ExpandButton = styled.button`
    background: none;
    border: none;
    color: #406edf;
    cursor: pointer;
    padding: 5px;

    @media (max-width: 768px) {
        display: block;
    }
`;

const Timeline = styled.div`
    margin-bottom: 25px;
    position: relative;

    @media (max-width: 768px) {
        margin-bottom: 20px;
        padding: 0 16px;
    }
`;

const TimelineContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;

    @media (min-width: 769px) {
        flex-direction: row;
        align-items: center;
        gap: 0;
        min-height: 80px;
        position: relative;
    }
`;

const TimelinePoints = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0px;

    @media (min-width: 769px) {
        flex-direction: row;
        gap: 0;
        height: 100%;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        gap: 15px;
    }
`;

const TimelinePoint = styled.div<{ isActive?: boolean; isTarget?: boolean }>`
    display: flex;
    flex-direction: row;
    align-items: center;
    position: relative;

    @media (min-width: 769px) {
        flex: 0 0 auto;
        position: relative;
        z-index: 3;
        flex-direction: column;
    }
`;

const DotWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    @media (min-width: 769px) {
        width: 48px;
        height: 48px;
    }
`;

const TimelineFirstDot = styled.div`
    box-sizing: border-box;
    border: 2px solid white;
    background-color: #0b6dff;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    filter: drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.15));

    @media (min-width: 769px) {
        width: 32px;
        height: 32px;
    }
`;

const TimelineMiddleDot = styled.div`
    box-sizing: border-box;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: linear-gradient(138.01deg, #ffffff 10.71%, #e7f0ff 95.77%);
    border: 2px solid rgba(11, 109, 255, 0.5);

    @media (min-width: 769px) {
        width: 28px;
        height: 28px;
    }
`;

const TimelineLastDot = styled.div`
    box-sizing: border-box;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background-color: #0b6dff;
    display: flex;
    justify-content: center;
    align-items: center;

    img {
        width: 75%;
    }
    @media (min-width: 769px) {
        width: 48px;
        height: 48px;
    }
`;

const TimelineLine = styled.div`
    background: #f6f6f8;
    border-radius: 173.303px;

    width: 8px;
    height: 24px;

    @media (max-width: 768px) {
        transform: translateX(14px);
    }

    @media (min-width: 768px) {
        height: 8px;
        width: auto;
        flex: 1;
        transform: translateY(-29px);
    }
`;

const TimelineLabel = styled.div<{ isActive?: boolean }>`
    color: ${(props) => (props.isActive ? "#1A1A1A" : "#7A7A7A")};
    text-align: center;
    margin-bottom: 4px;

    font-family: "Oswald";
    font-style: normal;
    font-weight: 600;
    font-size: 20px;
    line-height: 140%;
    margin-left: 16px;

    @media (min-width: 769px) {
        font-size: 28px;
        margin-left: 0;
    }
`;

const TimelineValue = styled.div<{ isNow?: boolean; isTarget?: boolean }>`
    font-family: "Lato";
    font-style: normal;
    line-height: 140%;
    margin-left: auto;

    font-weight: ${(props) => (props.isTarget ? "700" : "500")};
    font-size: ${(props) => (props.isTarget ? "20px" : "16px")};
    color: ${(props) =>
        props.isNow ? "#000" : props.isTarget ? "#0B6DFF" : "#7A7A7A"};

    @media (min-width: 769px) {
        color: ${(props) => (props.isTarget ? "#0B6DFF" : "#7A7A7A")};
        margin-left: 0;
    }
`;

const PlanFeatures = styled.div`
    margin-bottom: 30px;
    margin-top: 28px;

    @media (max-width: 769px) {
        border-top: 1px solid #d9d9d9;
    }

    @media (min-width: 769px) {
        margin-top: 80px;
    }
`;

const SeparatorContainer = styled.div`
    display: none;
    padding: 0 26px;
    margin-bottom: 44px;

    @media (min-width: 769px) {
        display: block;
    }
`;

const Separator = styled.div`
    border-top: 1px solid #d9d9d9;
`;

const FeaturesTitle = styled.h3`
    font-family: "Lato";
    font-style: normal;
    font-weight: 700;
    font-size: 16px;
    line-height: 115%;
    color: #000000;

    @media (min-width: 768px) {
        font-size: 24px;
    }
`;

const FeatureItem = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 15px;

    @media (max-width: 768px) {
        margin-bottom: 12px;
    }
`;

const FeatureIcon = styled.div`
    width: 20px;
    height: 20px;
    background: #406edf;
    border-radius: 50%;
    margin-right: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 10px;
`;

const FeatureText = styled.span`
    color: #1e1e1e;
    font-size: 16px;

    @media (min-width: 768px) {
        font-size: 20px;
    }
`;

const DesktopTrustIndicatorsContainer = styled.div`
    padding: 32px 0 49px;
    border-radius: 0 0 10px 10px;
`;

const MobileTrustIndicatorsContainer = styled.div`
    margin-top: 24px;
    margin-bottom: 8px;

    @media (min-width: 768px) {
        display: none;
    }
`;

const TrustIndicators = styled.div`
    display: flex;
    gap: 20px;
    margin-top: auto;

    @media (max-width: 768px) {
        gap: 15px;
    }
`;

const TrustItem = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    flex: 1;

    @media (max-width: 768px) {
        background-color: #fff;
        padding: 16px 8px;
        border-radius: 10px;
    }
`;

const TrustIcon = styled.div``;

const TrustText = styled.span`
    font-family: "Lato";
    font-style: normal;
    font-weight: 700;
    font-size: 12px;
    margin-top: 8px;

    color: #000000;

    @media (min-width: 768px) {
        font-size: 14.4px;
    }
`;

const OrderSummaryCard = styled.div`
    box-sizing: border-box;
    background: #ffffff;
    /* card shadow */
    box-shadow: 0px 5px 10px rgba(0, 0, 0, 0.08);
    border-radius: 10px;
    padding: 16px;

    @media (min-width: 768px) {
        padding: 25px;
    }
`;

const OrderSummaryHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
`;

const OrderSummaryTitle = styled.h3`
    font-size: 16px;
    font-weight: bold;
    color: #1e1e1e;
    margin: 0;

    @media (min-width: 768px) {
        font-size: 24px;
    }
`;

const FreeTrialBadge = styled.div`
    background: #047c2b;
    border-radius: 5px;
    color: white;
    padding: 8px 16px;
    font-size: 12px;
    font-weight: bold;

    @media (min-width: 768px) {
        font-size: 14px;
    }
`;

const PriceRow = styled.div<{ white?: boolean }>`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    background-color: ${(props) => (props.white ? "#fff" : "transparent")};
    border-radius: 10px;
    padding: 16px 0;
`;

// Left label/subtext column — shrink + wrap so a long subtext never collides
// with the price on narrow screens.
const PriceLabelGroup = styled.div`
    flex: 1;
    min-width: 0;
`;

const PriceSeparator = styled.div`
    border-bottom: 1px solid #e0e0e0;
    margin-top: 24px;

    @media (min-width: 768px) {
        margin-top: 16px;
    }
`;

const PriceLabel = styled.span`
    color: #1e1e1e;
    font-size: 16px;

    @media (min-width: 768px) {
        font-size: 20px;
    }
`;

const PriceValue = styled.span<{ isHighlight?: boolean }>`
    font-family: "Lato";
    font-style: normal;
    font-weight: 700;
    font-size: 32px;
    line-height: 115%;
    color: #047c2b;

    font-size: ${(props) => (props.isHighlight ? "24px" : "20px")};
    color: ${(props) => (props.isHighlight ? "#047C2B" : "#000")};

    @media (min-width: 768px) {
        font-size: ${(props) => (props.isHighlight ? "32px" : "20px")};
    }
`;

const PriceCluster = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    flex-shrink: 0;
`;

const OriginalPrice = styled.span`
    font-family: "Lato";
    font-style: normal;
    font-weight: 500;
    font-size: 18px;
    line-height: 115%;
    color: #9a9a9a;
    text-decoration: line-through;

    @media (min-width: 768px) {
        font-size: 22px;
    }
`;

const PriceSubtext = styled.span`
    margin-top: 5px;
    display: block;
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    line-height: 115%;
    text-align: left;
    color: #7a7a7a;
`;

const ReminderBox = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 13px 16px;
    gap: 12px;

    background: #d7e7fe;
    border-radius: 10px;
`;

const ReminderIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
`;

const ReminderText = styled.span`
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    line-height: 140%;
    color: #000000;

    @media (min-width: 768px) {
        font-size: 16px;
    }
`;

const PromoCodeContainer = styled.div`
    margin-top: 16px;
    position: relative;
`;

const PromoCodeInput = styled.input<{ disabled?: boolean; hasError?: boolean }>`
    width: 100%;
    padding: 13px 90px 13px 16px;
    border: 1px solid ${(props) => (props.hasError ? "#ff4444" : "#e0e0e0")};
    border-radius: 10px;
    font-size: 14px;
    box-sizing: border-box;
    background-color: ${(props) => (props.disabled ? "#f6f6f8" : "#ffffff")};
    
    &:focus {
        outline: none;
        border-color: ${(props) => (props.hasError ? "#ff4444" : "#406edf")};
    }

    @media (min-width: 768px) {
        font-size: 16px;
    }
`;

const PromoCodeError = styled.div`
    color: #ff4444;
    font-size: 12px;
    margin-top: 8px;
    
    @media (min-width: 768px) {
        font-size: 14px;
    }
`;

const PromoCodeButton = styled.button<{ isApplied?: boolean }>`
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    background: transparent;
    border: none;
    color: ${(props) => (props.isApplied ? "#ff4444" : "#406edf")};
    font-weight: 600;
    font-size: 14px;
    padding: 8px 12px;
    cursor: pointer;
    border-radius: 6px;
    transition: background-color 0.2s;

    &:hover {
        background-color: ${(props) => 
            props.isApplied ? "rgba(255, 68, 68, 0.1)" : "rgba(64, 110, 223, 0.1)"};
    }

    @media (min-width: 768px) {
        font-size: 16px;
    }
`;

const PromoCodeSpinner = styled.div`
    position: absolute;
    right: 16px;
    top: 50%;
    transform: translateY(-50%);
    width: 20px;
    height: 20px;
    border: 2px solid #f3f3f3;
    border-top: 2px solid #406edf;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;

    @keyframes spin {
        0% { transform: translateY(-50%) rotate(0deg); }
        100% { transform: translateY(-50%) rotate(360deg); }
    }
`;

const Spinner = styled.div`
    width: 20px;
    height: 20px;
    border: 2px solid #f3f3f3;
    border-top: 2px solid #406edf;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;

    @keyframes spin {
        0% { transform: translateY(-50%) rotate(0deg); }
        100% { transform: translateY(-50%) rotate(360deg); }
    }
`;

const PaymentSection = styled.div`
    margin-bottom: 25px;
`;

const PaymentTitle = styled.h3`
    font-style: normal;
    font-weight: 700;
    font-size: 16px;
    line-height: 140%;
    color: #000000;
    margin: 24px 0 16px 0;

    @media (min-width: 768px) {
        font-size: 24px;
        margin: 36px 0 24px 0;
    }
`;

const PaymentButtons = styled.div`
    display: flex;
    flex-direction: column;
    gap: 15px;
`;

const TermsText = styled.p`
    text-align: center;
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    line-height: 115%;
    color: #000000;

    @media (min-width: 768px) {
        font-size: 14px;
    }
`;

const MobileHeader = styled.div<{ isExpanded: boolean }>`
    display: none;

    @media (max-width: 768px) {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 20px;
        border-radius: 12px;
    }
`;

const MobileHeaderContent = styled.div<{ isExpanded: boolean }>`
    display: ${(props) => (props.isExpanded ? "block" : "none")};
    animation: ${(props) => (props.isExpanded ? "slideDown" : "slideUp")} 0.3s
        ease;
    width: 100%;

    @keyframes slideDown {
        from {
            opacity: 0;
            transform: translateY(-10px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    @keyframes slideUp {
        from {
            opacity: 1;
            transform: translateY(0);
        }
        to {
            opacity: 0;
            transform: translateY(-10px);
        }
    }
`;

const StripeText = styled.p`
    text-align: center;
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    line-height: 115%;
    color: #000000;
`;

const ActivePromotionBadge = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 16px;
    padding: 10px 14px;
    background-color: #f2f4f7;
    color: #5f6368;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;

    @media (min-width: 768px) {
        font-size: 16px;
    }
`;

const ActivePromotionIcon = styled.span`
    display: flex;
    align-items: center;
    font-size: 16px;
`;


const Checkmark = () => {
    return (
        <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <rect width="24" height="24" rx="11.6463" fill="#0B6DFF" />
            <path
                d="M9.8916 17.3715L4.78906 12.269L6.0647 10.9933L9.8916 14.8202L18.1049 6.60693L19.3805 7.88257L9.8916 17.3715Z"
                fill="white"
            />
        </svg>
    );
};

const CheckoutPageInner: React.FC<CheckoutPageInnerProps> = ({
    user,
    plan,
    isNoTrialVariation,
    onReinitializeCheckout,
    promoCode,
    setPromoCode,
    isPromoApplied,
    setIsPromoApplied,
    promoError,
    setPromoError
}) => {
    const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [isPromoAppliedLoading, setIsPromoAppliedLoading] = useState(false);

    const checkout = useCheckout();
    console.log("checkout", promoCode, isPromoApplied, checkout);
    const { titlePrediction, titlePredictionDate, timeToGoal } =
        useChartCalculations(user);
    const transformationData = getTransformationData(
        user,
        timeToGoal,
        CDN_ROOT
    );

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        checkMobile();
        window.addEventListener("resize", checkMobile);

        return () => window.removeEventListener("resize", checkMobile);
    }, []);

    const handlePromoCodeButton = async () => {
        if (checkout.type !== "success") {
            return;
        }

        if (isPromoApplied) {
            // Remove code - reinitialize without promo code
            try {
                setIsPromoAppliedLoading(true);
                const result =await checkout.checkout.removePromotionCode();
                if (result.type === "success") {
                    setPromoError("");
                    setIsPromoApplied(false);
                    setPromoCode("");
                    setPromoError("");
                } else {
                    setPromoError("Failed to remove promo code");
                }
            } catch (error) {
                console.log("error", error);
                setPromoError("An error occurred");
            } finally {
                setIsPromoAppliedLoading(false);
            }
        } else {
            // Apply code
            if (promoCode.trim()) {
                // web29_trial_toggle: NOTRIAL20A/NOTRIAL20M are reserved for the
                // pay-now (no-trial) offer. Block them in the trial flow so they
                // can't be hand-entered there; any other code is still allowed.
                const RESERVED_NO_TRIAL_CODES = ["NOTRIAL20A", "NOTRIAL20M"];
                if (
                    !isNoTrialVariation &&
                    RESERVED_NO_TRIAL_CODES.includes(promoCode.trim().toUpperCase())
                ) {
                    setPromoError("This code isn’t valid for the free trial.");
                    setIsPromoApplied(false);
                    return;
                }
                try {
                    setIsPromoAppliedLoading(true);
                    setPromoError("");
                    
                    const response = await fetch(
                        `${getInternalCoreEndpoint()}/internal/public/checkout/custom-apply-promo`,
                        {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                            },
                            body: JSON.stringify({
                                promotionCode: promoCode.trim(),
                                subscriptionPriceId: plan.priceId,
                                unlockProductId: plan.unlockProductId,
                                trialProductId: plan.trialProductId,
                                prefillEmail: user.email?.trim() || undefined,
                            }),
                        }
                    );

                    if (response.ok) {
                        const data = await response.json();
                        if (data.data.client_secret) {
                            onReinitializeCheckout(data.data.client_secret);
                            setIsPromoApplied(true);
                            setPromoError("");
                        }
                    } else {
                        // 4xx error means invalid promo code
                        const errorData = await response.json().catch(() => ({}));
                        console.log("Invalid promo code", response.status, errorData);
                        setPromoError(errorData.message || "Invalid promo code");
                        setIsPromoApplied(false);
                    }
                } catch (error) {
                    console.log("error", error);
                    setPromoError("An error occurred");
                    setIsPromoApplied(false);
                } finally {
                    setIsPromoAppliedLoading(false);
                }
            }
        }
    };

    const timeline = (
        <Timeline>
            <TimelineContainer>
                <TimelinePoints>
                    {transformationData.map((item, index) => (
                        <React.Fragment key={item.id}>
                            <TimelinePoint>
                                <DotWrapper>
                                    {index === 0 && <TimelineFirstDot />}
                                    {index ===
                                        transformationData.length - 1 && (
                                        <TimelineLastDot>
                                            <img
                                                src={trophyImage}
                                                alt="trophy"
                                            />
                                        </TimelineLastDot>
                                    )}
                                    {index !== 0 &&
                                        index !==
                                            transformationData.length - 1 && (
                                            <TimelineMiddleDot />
                                        )}
                                </DotWrapper>
                                <TimelineLabel isActive>
                                    {item.timelineLabel}
                                </TimelineLabel>
                                <TimelineValue isNow>
                                    {item.labelbodyfat}
                                </TimelineValue>
                            </TimelinePoint>
                            {index !== transformationData.length - 1 && (
                                <TimelineLine />
                            )}
                        </React.Fragment>
                    ))}
                </TimelinePoints>
            </TimelineContainer>
        </Timeline>
    );

    const infoBoxAndPlanFeatures = (
        <>
            <PlanFeatures>
                <FeaturesTitle>Your Custom Plan Includes:</FeaturesTitle>
                <FeatureItem>
                    <FeatureIcon>
                        <Checkmark />
                    </FeatureIcon>
                    <FeatureText>
                        Adaptive program that evolves with your progress
                    </FeatureText>
                </FeatureItem>
                <FeatureItem>
                    <FeatureIcon>
                        <Checkmark />
                    </FeatureIcon>
                    <FeatureText>
                        Personalized workout and nutrition coaching
                    </FeatureText>
                </FeatureItem>
                <FeatureItem>
                    <FeatureIcon>
                        <Checkmark />
                    </FeatureIcon>
                    <FeatureText>
                        All fitness and nutrition tools in one place
                    </FeatureText>
                </FeatureItem>
            </PlanFeatures>
        </>
    );

    const trustIndicators = (
        <>
            <SeparatorContainer>
                <Separator />
            </SeparatorContainer>
            <TrustIndicators>
                <TrustItem>
                    <TrustIcon>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="19"
                            height="21"
                            viewBox="0 0 19 21"
                            fill="none"
                        >
                            <path
                                d="M6.375 9.4585L8.4585 11.5415L12.625 7.375"
                                stroke="#0B6DFF"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                            <path
                                d="M9.49804 19.875L10.4145 19.479C12.3362 18.6544 13.9956 17.3197 15.213 15.6195C16.4304 13.9193 17.1594 11.9184 17.321 9.8335L17.769 4.0835C17.7763 3.84537 17.7017 3.61196 17.5575 3.42229C17.4133 3.23262 17.2084 3.0982 16.977 3.0415L9.49804 1.125L2.01904 3C1.78773 3.05668 1.58288 3.19103 1.43873 3.3806C1.29458 3.57016 1.21985 3.80346 1.22704 4.0415L1.67504 9.7915C1.83658 11.8765 2.56554 13.8775 3.78295 15.5778C5.00036 17.2781 6.65981 18.6128 8.58154 19.4375L9.49804 19.875Z"
                                stroke="#0B6DFF"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </TrustIcon>
                    <TrustText>Secure Payment</TrustText>
                </TrustItem>
                <TrustItem>
                    <TrustIcon>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="25"
                            viewBox="0 0 24 25"
                            fill="none"
                        >
                            <path
                                d="M18.838 8.375C18.7007 10.2814 17.2864 11.75 15.7443 11.75C14.2021 11.75 12.7855 10.2819 12.6505 8.375C12.5099 6.39172 13.8866 5 15.7443 5C17.6019 5 18.9786 6.42781 18.838 8.375Z"
                                stroke="#0B6DFF"
                                stroke-width="1.3125"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                            <path
                                d="M15.7498 14.75C12.695 14.75 9.75731 16.2673 9.02138 19.2223C8.92388 19.6133 9.16903 20 9.57075 20H21.9293C22.3311 20 22.5748 19.6133 22.4787 19.2223C21.7428 16.22 18.8051 14.75 15.7498 14.75Z"
                                stroke="#0B6DFF"
                                stroke-width="1.3125"
                                stroke-miterlimit="10"
                            />
                            <path
                                d="M9.36715 9.21594C9.25746 10.7384 8.11465 11.9375 6.88277 11.9375C5.6509 11.9375 4.50621 10.7389 4.3984 9.21594C4.28637 7.63203 5.39871 6.5 6.88277 6.5C8.36683 6.5 9.47918 7.66109 9.36715 9.21594Z"
                                stroke="#0B6DFF"
                                stroke-width="1.3125"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                            <path
                                d="M9.65671 14.8434C8.81061 14.4557 7.87874 14.3066 6.89108 14.3066C4.45358 14.3066 2.10515 15.5184 1.51686 17.8785C1.43952 18.1907 1.63546 18.4996 1.95608 18.4996H7.21921"
                                stroke="#0B6DFF"
                                stroke-width="1.3125"
                                stroke-miterlimit="10"
                                stroke-linecap="round"
                            />
                        </svg>
                    </TrustIcon>
                    <TrustText>200,000+ Members</TrustText>
                </TrustItem>
                <TrustItem>
                    <TrustIcon>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="25"
                            height="25"
                            viewBox="0 0 25 25"
                            fill="none"
                        >
                            <path
                                d="M15.4994 10.5002L11.4994 14.5002L9.49937 12.5002M13.7454 3.95919L14.9664 4.99919C15.2744 5.26119 15.6564 5.41919 16.0584 5.45219L17.6584 5.58019C18.1131 5.61651 18.5401 5.81357 18.8628 6.13607C19.1855 6.45858 19.3828 6.88544 19.4194 7.34019L19.5464 8.94019C19.5794 9.34319 19.7384 9.72619 20.0004 10.0332L21.0404 11.2532C21.3368 11.6007 21.4996 12.0425 21.4996 12.4992C21.4996 12.9559 21.3368 13.3977 21.0404 13.7452L20.0004 14.9662C19.7384 15.2742 19.5794 15.6562 19.5474 16.0592L19.4194 17.6592C19.3831 18.114 19.186 18.5409 18.8635 18.8636C18.541 19.1863 18.1141 19.3836 17.6594 19.4202L16.0594 19.5482C15.6565 19.5801 15.2741 19.7383 14.9664 20.0002L13.7454 21.0402C13.3979 21.3366 12.9561 21.4994 12.4994 21.4994C12.0426 21.4994 11.6009 21.3366 11.2534 21.0402L10.0334 20.0002C9.7254 19.7381 9.34253 19.5799 8.93937 19.5482L7.33937 19.4202C6.88446 19.3836 6.45746 19.1861 6.13494 18.8633C5.81241 18.5404 5.61545 18.1131 5.57937 17.6582L5.45137 16.0592C5.41898 15.6566 5.26046 15.2745 4.99837 14.9672L3.95837 13.7452C3.6625 13.3978 3.5 12.9565 3.5 12.5002C3.5 12.0439 3.6625 11.6025 3.95837 11.2552L4.99837 10.0332C5.26137 9.72519 5.41837 9.34319 5.45037 8.94019L5.57837 7.34119C5.61477 6.88595 5.81226 6.45859 6.13538 6.13584C6.4585 5.81309 6.88608 5.61609 7.34137 5.58019L8.93937 5.45319C9.3423 5.42101 9.7248 5.26248 10.0324 5.00019L11.2534 3.96019C11.6009 3.66379 12.0426 3.50098 12.4994 3.50098C12.9561 3.50098 13.3979 3.66279 13.7454 3.95919Z"
                                stroke="#0B6DFF"
                                stroke-width="1.66667"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </TrustIcon>
                    <TrustText>{isNoTrialVariation ? "30-day Money-Back Guarantee" : "14-day Free Trial"}</TrustText>
                </TrustItem>
            </TrustIndicators>
        </>
    );

    const discount = checkout.type === "success" ? checkout.checkout.discountAmounts?.[0] : null;
    // need a better way to check for active promotion
    const appliedDiscountName = discount && discount.displayName;
    const promotionDiscountNames = [
        "STUDENTANNUAL",
        "STUDENTMONTHLY",
        "STUDENT-ANNUAL",
        "STUDENT-MONTHLY",
    ];
    // web29_trial_toggle: the no-trial path pre-applies SPECIAL20 (20% off) on the
    // subscription line item. Surface it as the applied discount.
    const noTrialDiscount = (() => {
        if (!isNoTrialVariation || checkout.type !== "success") return null;
        const item = checkout.checkout.lineItems?.find((it: any) => !!it.unitAmount);
        return item?.discountAmounts?.[0] ?? null;
    })();
    const noTrialDiscountLabel = noTrialDiscount
        ? noTrialDiscount.displayName || (noTrialDiscount.percentOff ? `${noTrialDiscount.percentOff}% off applied` : "Discount applied")
        : null;

    const hasActivePromotion =
        !!noTrialDiscount ||
        promotionDiscountNames.includes(appliedDiscountName?.toLocaleUpperCase() ?? "");

    // web29_trial_toggle: no-trial path charges the first period today (20% off).
    const getDueTodayAmount = () => {
        if (checkout.type !== "success") return null;
        const item = checkout.checkout.lineItems.find((it: any) => !!it.unitAmount);
        if (!item) return null;
        const pct = item.discountAmounts?.[0]?.percentOff ?? 0;
        let amt = item.unitAmount.minorUnitsAmount / 100;
        if (pct > 0) amt = Math.floor(amt * (1 - pct / 100) * 100) / 100;
        return `$${amt.toFixed(2)}`;
    };

    // web29_trial_toggle: the undiscounted price shown struck-through next to the
    // discounted Due Today amount. For a single plan that's the regular price of
    // this subscription. For a gym-buddy plan we show the regular price of TWO
    // separate plans (2 x the regular per-person rate, annualized for committed),
    // so the strike→due-today drop equals the compounded % on the badge.
    const getOriginalAmount = () => {
        if (checkout.type !== "success") return null;
        const item = checkout.checkout.lineItems.find((it: any) => !!it.unitAmount);
        if (!item) return null;
        const pct = item.discountAmounts?.[0]?.percentOff ?? 0;
        if (!pct) return null; // no discount → nothing to strike through
        if (plan?.isMultiUser) {
            const periods = plan?.id === "committed" ? 12 : 1; // committed bills annually
            const twoPlans = (plan?.priceRaw ?? 0) * 2 * periods;
            if (!twoPlans) return null;
            return `$${twoPlans.toFixed(2)}`;
        }
        return `$${(item.unitAmount.minorUnitsAmount / 100).toFixed(2)}`;
    };

    // web29_trial_toggle: surface the SAME compounded savings the pricing page
    // shows (20% single, 24% gym-buddy monthly, 33% gym-buddy annual) instead of
    // the flat 20% Stripe applies, plus a friendly display code in place of the
    // raw NOTRIAL20* (the real promo code sent to Stripe is unchanged).
    const noTrialSavingsPercent = () => {
        const toNum = (v?: string) =>
            parseFloat(String(v ?? "").replace(/[^0-9.]/g, "")) || 0;
        const discounted = toNum(plan?.price) * 0.8;
        const single = plan?.isMultiUser
            ? getPlans().find((p) => p.id === plan?.id)
            : plan;
        const baseline = toNum(single?.price);
        if (!discounted || !baseline) return 20;
        return Math.round((1 - discounted / baseline) * 100);
    };
    const friendlyNoTrialCode = () =>
        `SAVE${noTrialSavingsPercent()}${plan?.id === "committed" ? "ANNUAL" : "MONTHLY"}`;

    // web29_trial_toggle: the 20% off only covers the first year, so spell out the
    // full price the subscription renews at afterward. unitAmount is the
    // undiscounted amount; interval comes from the plan (committed = annual).
    const getRenewalText = () => {
        if (checkout.type !== "success") return "";
        const item = checkout.checkout.lineItems.find((it: any) => !!it.unitAmount);
        if (!item) return "";
        const full = item.unitAmount.minorUnitsAmount / 100;
        const interval = plan?.id === "committed" ? "year" : "month";
        return ` After the first year, it renews at $${full.toFixed(2)}/${interval}.`;
    };

    return (
        <Wrapper>
            <CheckoutPageContainer>
                <MainContent>
                    <LeftPanel>
                        <PersonalPlanCard>
                            <PersonalPlanCardInner>
                                <PlanTitle>
                                    {user?.name ? `${user.name}'s` : ""}{" "}
                                    Personal Plan
                                </PlanTitle>

                                <TargetGoal>
                                    {titlePrediction} {titlePredictionDate}
                                    <ExpandButton
                                        onClick={() =>
                                            setIsHeaderExpanded(
                                                !isHeaderExpanded
                                            )
                                        }
                                        style={{
                                            display: isMobile
                                                ? "block"
                                                : "none",
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            icon={
                                                isHeaderExpanded
                                                    ? faChevronUp
                                                    : faChevronDown
                                            }
                                        />
                                    </ExpandButton>
                                </TargetGoal>

                                {isHeaderExpanded && (
                                    <MobileHeader isExpanded={isHeaderExpanded}>
                                        <MobileHeaderContent
                                            isExpanded={isHeaderExpanded}
                                        >
                                            {timeline}
                                            {infoBoxAndPlanFeatures}
                                        </MobileHeaderContent>
                                    </MobileHeader>
                                )}

                                {!isMobile && (
                                    <>
                                        {timeline}
                                        {infoBoxAndPlanFeatures}
                                    </>
                                )}
                            </PersonalPlanCardInner>
                            {!isMobile && (
                                <DesktopTrustIndicatorsContainer>
                                    {trustIndicators}
                                </DesktopTrustIndicatorsContainer>
                            )}
                        </PersonalPlanCard>
                    </LeftPanel>

                    <RightPanel>
                        <OrderSummaryCard>
                            <OrderSummaryHeader>
                                <OrderSummaryTitle>
                                    Order Summary
                                </OrderSummaryTitle>
                                <FreeTrialBadge>{isNoTrialVariation ? "30-DAY GUARANTEE" : "FREE TRIAL"}</FreeTrialBadge>
                            </OrderSummaryHeader>

                            <PriceRow white>
                                <PriceLabelGroup>
                                    <PriceLabel>Due Today</PriceLabel>
                                    <PriceSubtext>
                                        {isNoTrialVariation
                                            ? `${noTrialSavingsPercent()}% off your first year - billed today, refundable for 30 days.`
                                            : "No payment today - try everything free for 14 days"}
                                    </PriceSubtext>
                                </PriceLabelGroup>
                                <PriceCluster>
                                    {isNoTrialVariation && getOriginalAmount() && (
                                        <OriginalPrice>{getOriginalAmount()}</OriginalPrice>
                                    )}
                                    <PriceValue isHighlight>
                                        {isNoTrialVariation ? (getDueTodayAmount() ?? "—") : "$0.00"}
                                    </PriceValue>
                                </PriceCluster>
                            </PriceRow>
                            {checkout.type === "success" && (
                                <>
                                    {!isNoTrialVariation && (
                                        <>
                                            <PriceSeparator />
                                            <PriceRow>
                                                <AfterTrialPeriod user={user} />
                                            </PriceRow>
                                        </>
                                    )}

                                    <ReminderBox>
                                        <ReminderIcon>{isNoTrialVariation ? "🛡️" : "🔔"}</ReminderIcon>
                                        <ReminderText>
                                            {isNoTrialVariation
                                                ? "Risk-free 30-day money-back guarantee. If you don't see real results, we'll refund every penny."
                                                : "No surprises - we'll remind you 2 days before your trial ends"}
                                        </ReminderText>
                                    </ReminderBox>

                                    {hasActivePromotion && (appliedDiscountName || noTrialDiscountLabel) && (
                                        <ActivePromotionBadge>
                                            <ActivePromotionIcon>🏷️</ActivePromotionIcon>
                                            {isNoTrialVariation
                                                ? friendlyNoTrialCode()
                                                : (appliedDiscountName || noTrialDiscountLabel)}
                                        </ActivePromotionBadge>
                                    )}
                                    {!hasActivePromotion && (
                                        <>
                                            <PromoCodeContainer>
                                                <PromoCodeInput
                                                    type="text"
                                                    placeholder="Enter promo code"
                                                    value={promoCode}
                                                    onChange={(e) => {
                                                        setPromoCode(e.target.value);
                                                        setPromoError("");
                                                    }}
                                                    disabled={isPromoApplied || isPromoAppliedLoading}
                                                    hasError={!!promoError}
                                                />
                                                {isPromoAppliedLoading ? (
                                                    <PromoCodeSpinner />
                                                ) : (
                                                    <PromoCodeButton
                                                        isApplied={isPromoApplied}
                                                        onClick={handlePromoCodeButton}
                                                        type="button"
                                                        disabled={checkout.type !== "success"}
                                                    >
                                                        {isPromoApplied ? "Remove code" : "Apply"}
                                                    </PromoCodeButton>
                                                )}
                                            </PromoCodeContainer>
                                            {promoError && <PromoCodeError>{promoError}</PromoCodeError>}
                                        </>
                                    )}
                                </>
                            )}
                            {checkout.type === "loading" && (
                              <PriceRow white>
                                <Spinner />
                              </PriceRow>
                            )}
                        </OrderSummaryCard>

                        <PaymentSection>
                            <PaymentTitle>Complete Your Checkout</PaymentTitle>
                            <PaymentButtons>
                                <CheckoutForm />
                            </PaymentButtons>
                        </PaymentSection>

                        {isMobile && (
                            <MobileTrustIndicatorsContainer>
                                {trustIndicators}
                            </MobileTrustIndicatorsContainer>
                        )}

                        <TermsText>
                            {isNoTrialVariation
                                ? `By completing your purchase, you agree to our Terms of Service and Privacy Policy. You'll be charged today with ${noTrialSavingsPercent()}% off, and can request a full refund within 30 days.${getRenewalText()}`
                                : "By completing your purchase, you agree to our Terms of Service and Privacy Policy. Your 14-day free trial starts today. You can cancel anytime before the trial ends."}
                        </TermsText>
                        <StripeText>Powered by Stripe</StripeText>
                    </RightPanel>
                </MainContent>
            </CheckoutPageContainer>
        </Wrapper>
    );
};

export const CheckoutPage: React.FC<CheckoutPageProps> = (props) => {
    const plan = useCheckoutPlan();
    const { client } = useStatsigClient();
    const navigate = useNavigate();

    const web17_3Experiment = client?.getExperiment("web17_3");
    const web17_3Arm = web17_3Experiment?.value["arm"];
    const isTeen = props.user.age === Age.Teens;
    const isWeb17_3Variation = isTeen && web17_3Arm === 'variation';


    console.log("isWeb17_3Variation", isWeb17_3Variation);
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    // web29_trial_toggle: ?trial=false → no trial, immediate charge + 20% off promo.
    const isNoTrialVariation = searchParams.get("trial") === "false";
    const stripePublishableKey = useMemo(
        () => getStripePublishableKey(location.search),
        [location.search]
    );
    const stripePromise = useMemo(
        () => loadStripe(stripePublishableKey),
        [stripePublishableKey]
    );

    const eventID = useMemo(() => searchParams.get("fb_event_id") || "", [searchParams]);

    const [checkoutKey, setCheckoutKey] = useState(0);
    const [clientSecret, setClientSecret] = useState<string | null>(null);
    
    const [promoCode, setPromoCode] = useState("");
    const [isPromoApplied, setIsPromoApplied] = useState(false);
    const [promoError, setPromoError] = useState<string>("");

    const fetchClientSecret = useMemo(() => {
        // If we have a custom client secret (from promo code), use it
        if (clientSecret) {
            console.log("using client secret from state", clientSecret);
            return () => Promise.resolve(clientSecret);
        }
        // Otherwise use the default function
        console.log("using default client secret function");
        return createGetClientSecretPromise(plan, eventID, props.user, isWeb17_3Variation, isNoTrialVariation);
    }, [plan, clientSecret, eventID, props.user, isWeb17_3Variation, isNoTrialVariation]);

    const handleReinitializeCheckout = (newClientSecret: string) => {
        setClientSecret(newClientSecret);
        setCheckoutKey(prev => prev + 1); // Force remount
    };

    useEffect(() => {
      if (!props.user || !props.user.focus) {
        navigate("/");
    }
    }, [props.user, navigate]);

    useEffect(() => {
        track("InitiateCheckout", undefined, { eventID });
        amplitude.track("CheckoutPageViewed", { planId: plan?.id });
    }, [plan, eventID]);
   

    if (!plan) {
        return null;
    }

    return (
        <CheckoutProvider
            key={checkoutKey}
            stripe={stripePromise}
            options={{
                fetchClientSecret,
                elementsOptions: { appearance: { theme: "stripe" } },
            }}
        >
            <CheckoutPageInner
                {...props}
                plan={plan}
                isNoTrialVariation={isNoTrialVariation}
                onReinitializeCheckout={handleReinitializeCheckout}
                promoCode={promoCode}
                setPromoCode={setPromoCode}
                isPromoApplied={isPromoApplied}
                setIsPromoApplied={setIsPromoApplied}
                promoError={promoError}
                setPromoError={setPromoError}
            />

        </CheckoutProvider>
    );
};

export default CheckoutPage;
